import { createClient, type SupabaseClient } from '@supabase/supabase-js'

import { loadDesktopSessionSnapshot } from '@/lib/desktop-session-access'
import { isDesktopHost } from '@/lib/desktop-host'
import type { Database } from '@/supabase/types'

let desktopAccessTokenClient: SupabaseClient<Database> | null = null
let cachedDesktopAccessToken:
  | {
      value: string | null
      expiresAt: number
    }
  | null = null
let pendingDesktopAccessTokenPromise: Promise<string | null> | null = null

const DESKTOP_ACCESS_TOKEN_CACHE_TTL_MS = 3000
const DESKTOP_EMPTY_ACCESS_TOKEN_CACHE_TTL_MS = 250

async function loadAccessTokenFromDesktopSession(): Promise<string | null> {
  const desktopSession = await loadDesktopSessionSnapshot({
    includeTokens: true
  })

  const accessToken = desktopSession?.sessionData?.accessToken?.trim()
  if (!desktopSession?.isAuthenticated || !accessToken || desktopSession?.error) {
    return null
  }

  return accessToken
}

export async function getDesktopAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined' || !isDesktopHost()) {
    return null
  }

  if (cachedDesktopAccessToken && cachedDesktopAccessToken.expiresAt > Date.now()) {
    return cachedDesktopAccessToken.value
  }

  if (pendingDesktopAccessTokenPromise) {
    return pendingDesktopAccessTokenPromise
  }

  pendingDesktopAccessTokenPromise = loadAccessTokenFromDesktopSession()
    .catch((error) => {
      console.warn('Failed to load desktop access token for hosted Supabase client:', error)
      return null
    })
    .then((accessToken) => {
      cachedDesktopAccessToken = {
        value: accessToken,
        expiresAt:
          Date.now() +
          (accessToken
            ? DESKTOP_ACCESS_TOKEN_CACHE_TTL_MS
            : DESKTOP_EMPTY_ACCESS_TOKEN_CACHE_TTL_MS)
      }

      return accessToken
    })
    .finally(() => {
      pendingDesktopAccessTokenPromise = null
    })

  return pendingDesktopAccessTokenPromise
}

export async function getDesktopSessionUserId(): Promise<string | null> {
  if (typeof window === 'undefined' || !isDesktopHost()) {
    return null
  }

  try {
    const desktopSession = await loadDesktopSessionSnapshot({
      includeTokens: false
    })
    const userId = desktopSession?.user?.id?.trim()

    if (!desktopSession?.isAuthenticated || !userId || desktopSession?.error) {
      return null
    }

    return userId
  } catch (error) {
    console.warn('Failed to load desktop session user id:', error)
    return null
  }
}

export function getDesktopAccessTokenSupabaseClient(): SupabaseClient<Database> | null {
  if (typeof window === 'undefined' || !isDesktopHost()) {
    return null
  }

  if (desktopAccessTokenClient) {
    return desktopAccessTokenClient
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  if (!supabaseUrl || !supabaseAnonKey) {
    return null
  }

  desktopAccessTokenClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    accessToken: async () => (await getDesktopAccessToken()) || '',
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false
    }
  })

  return desktopAccessTokenClient
}

export async function withDesktopAccessTokenHeaders(headers?: HeadersInit): Promise<Headers> {
  const nextHeaders = new Headers(headers)
  const accessToken = await getDesktopAccessToken()
  if (!accessToken) {
    return nextHeaders
  }

  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim() || ''
  const currentAuthorization = nextHeaders.get('Authorization')

  if (!currentAuthorization || (anonKey && currentAuthorization === `Bearer ${anonKey}`)) {
    nextHeaders.set('Authorization', `Bearer ${accessToken}`)
  }

  return nextHeaders
}

export async function getDesktopAccessTokenHeaders(
  headers?: HeadersInit
): Promise<Record<string, string>> {
  const mergedHeaders = await withDesktopAccessTokenHeaders(headers)
  return Object.fromEntries(mergedHeaders.entries())
}

export function createDesktopAwareSupabaseProxy(
  fallbackClient: SupabaseClient<Database>
): SupabaseClient<Database> {
  return new Proxy(fallbackClient as object, {
    get(_target, prop) {
      const propertyName = typeof prop === 'string' ? prop : ''
      const preferredClient =
        propertyName === 'auth'
          ? fallbackClient
          : getDesktopAccessTokenSupabaseClient() || fallbackClient
      const value = Reflect.get(preferredClient as object, prop, preferredClient)

      return typeof value === 'function' ? value.bind(preferredClient) : value
    }
  }) as SupabaseClient<Database>
}
